import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../components/header";
import BlockBackButton from "../../utils/disabledBackButton";
import { useDispatch, useSelector } from "react-redux";
import { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid2";
import {
    Box,
    Card,
    Typography,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Stack,
    MenuItem,
    Checkbox,
    ListItemText,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import VehicleList from "../../components/Vehicles/Vehicles";
import {
    setActiveCarDetails,
    setSelectedDate,
} from "../../redux/reducers/service-reducer";
import { useParams } from "react-router";
import {
    createUserFromAdmin,
    deleteUserFromAdmin,
    getAllDevicesData,
    getUserListFromAdmin,
    updateUserFromAdmin,
} from "../../redux/actions/service-action";
import { formatFleetData } from "../../utils/methods";

interface User {
    id: string;
    email: string;
    role: string;
    status?: "ACTIVE" | "DISABLED";
    assigned_devices?: AssignedDevice[];
    [key: string]: any;
}

interface AssignedDevice {
    uuid: string;
    device_name: string;
}

interface UserFormState {
    full_name: string;
    email: string;
    password: string;
    phone: any;
    role: string;
    assigned_devices: AssignedDevice[];
}

const EMPTY_FORM: UserFormState = {
    full_name: "",
    email: "",
    phone: "",
    password: "",
    role: "USER",
    assigned_devices: [],
};

const COLORS = {
    total: { main: "#4F46E5", bg: "#D2D2D4" },
    active: { main: "#16A34A", bg: "#D2D2D4" },
    disabled: { main: "#DC2626", bg: "#D2D2D4" },
    admin: { main: "#7C3AED", bg: "#D2D2D4" },
};

const initials = (email: string) =>
    (email || "?").trim().charAt(0).toUpperCase();

const normalizeAssignedDevice = (d: any): AssignedDevice => ({
    uuid: d?.uuid ?? d?.device_uuid ?? d?.id ?? "",
    device_name: d?.device_name ?? d?.devicename ?? d?.name ?? "",
});

const StatCard = ({
    icon,
    label,
    value,
    color,
}: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: { main: string; bg: string };
}) => (
    <Card
        elevation={0}
        sx={{
            p: 2.5,
            borderRadius: "12px",
            border: "1px solid #DADCE3",
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
            overflow: "hidden",
            transition: "box-shadow 0.15s ease, border-color 0.15s ease",
            "&:hover": {
                borderColor: "#C7CAD4",
                boxShadow: "0 2px 8px rgba(16, 24, 40, 0.06)",
            },
            "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "4px",
                background: color.main,
            },
        }}
    >
        <Box
            sx={{
                width: 46,
                height: 46,
                borderRadius: "12px",
                background: color.bg,
                color: color.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: "#111827" }}>
                {value}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.3 }}>
                {label}
            </Typography>
        </Box>
    </Card>
);

const UserManagement = () => {
    BlockBackButton("");
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [fleetData, setFleetData] = useState<any>({});
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [videoPlayStatus, setVideoPlayStatus] = useState(false);
    const [openVehicleMenu, setOpenVehicleMenu] = useState(false);
    const allState = useSelector((state: any) => state);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const rowsPerPage = 5;
    const dispatch = useDispatch();

    const handleDateChange = async (newValue: Dayjs | null) => {
        dispatch(setSelectedDate(newValue));
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getUserListFromAdmin();
            if (res) {
                setUsers(Array.isArray(res) ? res : res.users || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const buildApiPayload = (payload: UserFormState) => ({
        full_name: payload.full_name,
        email: payload.email,
        phone: payload.phone,
        is_superuser: payload.role === "ADMIN",
        device_ids: payload.assigned_devices.map((device) => device.uuid),
    });
    
    const createUser = async (payload: UserFormState) => {
        const apiPayload: any = buildApiPayload(payload);
        apiPayload.password = payload.password;
        const res = await createUserFromAdmin(apiPayload);
        if (res) {
            await fetchUsers();
        }
        return res;
    };

    const updateUserRole = async (id: string, payload: UserFormState) => {
        const apiPayload = buildApiPayload(payload);
        const res = await updateUserFromAdmin(id, apiPayload);
        if (res) {
            await fetchUsers();
        }
        return res;
    };

    const deleteUser = async (id: string) => {
        const res = await deleteUserFromAdmin(id);
        if (res) {
            await fetchUsers();
        }
        return res;
    };

    const openCreateForm = useCallback(() => {
        setEditingUser(null);
        setForm(EMPTY_FORM);
        setIsFormOpen(true);
    }, []);

    const openEditForm = useCallback((user: User) => {
        setEditingUser(user);
        setForm({
            full_name: user.full_name ?? "",
            email: user.email ?? "",
            phone: user.phone ?? "",
            password: "",
            role: user.role ?? "USER",
            assigned_devices: Array.isArray(user.assigned_devices)
                ? user.assigned_devices.map(normalizeAssignedDevice)
                : [],
        });
        setIsFormOpen(true);
    }, []);

    const closeForm = useCallback(() => {
        setIsFormOpen(false);
        setEditingUser(null);
        setForm(EMPTY_FORM);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            if (editingUser) {
                await updateUserRole(editingUser.id, form);
            } else {
                await createUser(form);
            }
            closeForm();
        } catch (err: any) {
            setError(err?.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Delete this user? This cannot be undone.")) return;
        setError(null);
        try {
            await deleteUser(id);
        } catch (err: any) {
            setError(err?.message || "Failed to delete user");
        }
    };

    const handlePressCar = (e: any) => {
        dispatch(setActiveCarDetails(e));
    };

    const view = useSelector((state: any) => state.ui?.view || "Dashboard");
    const { deviceId, userId }: any = useParams();

    const getAllDevicesDataFunTest = async () => {
        try {
            const token = allState?.auth.loginDetails.user_details[0].access_token;
            const res: any = await getAllDevicesData(token);
            const formattedData: any = formatFleetData(res, "Delhi");
            setFleetData(formattedData);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getAllDevicesDataFunTest();
    }, []);

    const stats = useMemo(() => {
        const total = users.length;
        const disabled = users.filter((u) => u.status === "DISABLED").length;
        const active = total - disabled;
        const admins = users.filter((u) => u.role === "ADMIN").length;
        return { total, active, disabled, admins };
    }, [users]);

    const filteredUsers: any = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                u.email?.toLowerCase().includes(q) ||
                u.role?.toLowerCase().includes(q)
        );
    }, [users, searchQuery]);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const paginatedUsers: any = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredUsers.slice(start, start + rowsPerPage);
    }, [filteredUsers, page, rowsPerPage]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    return (
        <>
            <Header
                fleetData={fleetData}
                playingStatus={videoPlayStatus}
                profileDetails="PR"
                selectedDate={setSelectedDate}
                availableDates={availableDates}
                onDateChange={handleDateChange}
                onToggle={() => setIsCollapsed((prev) => !prev)}
                isCollapsed={isCollapsed}
                onMenuClick={() => setOpenVehicleMenu(true)}
            />
            <Grid container spacing={2}>
                <Grid
                    size={{ xs: 12, sm: 6, md: 2, lg: 2 }}
                    sx={{ margin: "5px 0px 0px 50px" }}
                >
                    <VehicleList
                        fleetData={fleetData}
                        onPressCar={(car) => {
                            handlePressCar(car);
                            setOpenVehicleMenu(false);
                        }}
                        isCollapsed={false}
                        showSearch={view === "Search"}
                        deviceId={deviceId}
                        userId={userId}
                        miceStatus={null}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 9, lg: 9.5 }}>
                    <Box sx={{ px: 0, py: 2, fontFamily: "inherit" }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2.5 }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
                                    User Management
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1.2}>
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchUsers}
                                        sx={{
                                            border: "1px solid #E7E9EF",
                                            borderRadius: "10px",
                                        }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    startIcon={<AddIcon />}
                                    onClick={openCreateForm}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: "10px",
                                        background: COLORS.total.main,
                                        px: 2.2,
                                        "&:hover": { background: "#4338CA" },
                                    }}
                                >
                                    Add User
                                </Button>
                            </Stack>
                        </Stack>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    icon={<PeopleAltOutlinedIcon />}
                                    label="Total Users"
                                    value={stats.total}
                                    color={COLORS.total}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    icon={<CheckCircleOutlineIcon />}
                                    label="Active Users"
                                    value={stats.active}
                                    color={COLORS.active}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    icon={<BlockOutlinedIcon />}
                                    label="Disabled Users"
                                    value={stats.disabled}
                                    color={COLORS.disabled}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <StatCard
                                    icon={<AdminPanelSettingsOutlinedIcon />}
                                    label="Admins"
                                    value={stats.admins}
                                    color={COLORS.admin}
                                />
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                                {error}
                            </Alert>
                        )}

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: "12px",
                                border: "1px solid #DADCE3",
                                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ p: 2, borderBottom: "1px solid #DADCE3", background: "#FAFAFB" }}>
                                <TextField
                                    size="small"
                                    placeholder="Search by email or role…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{
                                        width: 320,
                                        maxWidth: "100%",
                                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {loading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : (
                                <TableContainer>
                                    <Table
                                        sx={{
                                            borderCollapse: "collapse",
                                            "& td, & th": {
                                                border: "1px solid #d4d4d4",
                                            },
                                            "& tr:last-of-type td": {
                                                borderBottom: "none",
                                            },
                                            "& td:first-of-type, & th:first-of-type": {
                                                borderLeft: "none",
                                            },
                                            "& td:last-of-type, & th:last-of-type": {
                                                borderRight: "none",
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow sx={{ background: "#FAFAFB" }}>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Username
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Email
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Phone
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Active Session
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Assign Device
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Role
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}>
                                                    Status
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ fontWeight: 600, color: "#374151", borderTop: "none" }}
                                                >
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredUsers?.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#9CA3AF", border: "none" }}>
                                                        No users found.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (paginatedUsers?.map((user: any) => {
                                                const isDisabled = user.status === "DISABLED";
                                                return (
                                                    <TableRow key={user.id} hover>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 34,
                                                                        height: 34,
                                                                        fontSize: 14,
                                                                        fontWeight: 700,
                                                                        background: COLORS.total.bg,
                                                                        color: COLORS.total.main,
                                                                    }}
                                                                >
                                                                    {initials(user.full_name)}
                                                                </Avatar>
                                                                <Typography sx={{ fontSize: 14, color: "#111827" }}>
                                                                    {user.full_name}
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>

                                                        <TableCell>
                                                            <Typography sx={{ fontSize: 14, color: "#111827" }}>
                                                                {user.email}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell>
                                                            <Typography sx={{ fontSize: 14, color: "#111827" }}>
                                                                {user.phone}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell>
                                                            <Typography sx={{ fontSize: 14, color: "#111827" }}>
                                                                {user.active_sessions}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell>
                                                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                                {user?.assigned_devices?.length
                                                                    ? user.assigned_devices.map(
                                                                        (device: any, ind: number) => (
                                                                            <Chip
                                                                                key={device?.uuid ?? ind}
                                                                                label={device?.device_name}
                                                                                size="small"
                                                                                sx={{
                                                                                    fontSize: 12,
                                                                                    background: "#F3F4F6",
                                                                                    color: "#4B5563",
                                                                                }}
                                                                            />
                                                                        )
                                                                    )
                                                                    : (
                                                                        <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                                                                            —
                                                                        </Typography>
                                                                    )}
                                                            </Stack>
                                                        </TableCell>

                                                        <TableCell>
                                                            {user.is_superuser ? (
                                                                <Chip
                                                                    label="Admin"
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: 12,
                                                                        background:
                                                                            user.role === "ADMIN"
                                                                                ? COLORS.admin.bg
                                                                                : "#F3F4F6",
                                                                        color:
                                                                            user.role === "ADMIN"
                                                                                ? COLORS.admin.main
                                                                                : "#4B5563",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="User"
                                                                    size="small"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: 12,
                                                                        background: "#F3F4F6",
                                                                        color: "#4B5563",
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={
                                                                    isDisabled ? (
                                                                        <span className="status-badge warning">Disabled</span>
                                                                    ) : (
                                                                        <span className="status-badge success">Active</span>
                                                                    )
                                                                }
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: 12,
                                                                    background: "#F3F4F6",
                                                                    color: "#4B5563",
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="Edit role">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => openEditForm(user)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete user">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDelete(user.id)}
                                                                    sx={{ color: COLORS.disabled.main }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {!loading && filteredUsers.length > 0 && (
                                <TablePagination
                                    component="div"
                                    count={filteredUsers.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[5]}
                                    sx={{
                                        borderTop: "1px solid #DADCE3",
                                    }}
                                />
                            )}
                        </Paper>
                    </Box>
                </Grid>
            </Grid>

            <Dialog
                open={isFormOpen}
                onClose={closeForm}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: "16px" } }}
            >
                <form onSubmit={handleSubmit}>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
                        {editingUser ? "Edit User Details" : "Add New User"}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2.2} sx={{ mt: 0.5 }}>
                            <TextField
                                label="Name"
                                type="text"
                                fullWidth
                                required
                                value={form.full_name}
                                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                            />
                            <TextField
                                label="Email"
                                type="email"
                                fullWidth
                                required
                                value={form.email}
                                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                            />
                            <TextField
                                label="Phone"
                                type="text"
                                fullWidth
                                required
                                value={form.phone}
                                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                            />

                            <TextField
                                select
                                fullWidth
                                label="Assign Device"
                                SelectProps={{
                                    multiple: true,
                                    displayEmpty: true,
                                    value: form.assigned_devices.map((item) => item.uuid),
                                    onChange: (e) => {
                                        const selectedUuids = e.target.value as string[];

                                        const selectedDevices: AssignedDevice[] =
                                            fleetData?.Delhi?.fleet_devices
                                                ?.filter((item: any) =>
                                                    selectedUuids.includes(item.raw.uuid)
                                                )
                                                .map((item: any) => ({
                                                    uuid: item.raw.uuid,
                                                    device_name: item.devicename,
                                                })) ?? [];

                                        setForm((prev) => ({
                                            ...prev,
                                            assigned_devices: selectedDevices,
                                        }));
                                    },
                                    renderValue: (selected) => {
                                        const selectedUuids = selected as string[];

                                        if (selectedUuids.length === 0) {
                                            return <span style={{ color: "#9e9e9e" }}>Assign Device</span>;
                                        }

                                        return fleetData?.Delhi?.fleet_devices
                                            ?.filter((item: any) => selectedUuids.includes(item.raw.uuid))
                                            .map((item: any) => item.devicename)
                                            .join(", ");
                                    },
                                }}
                            >
                                {fleetData?.Delhi?.fleet_devices?.map((item: any) => (
                                    <MenuItem key={item.raw.uuid} value={item.raw.uuid}>
                                        <Checkbox
                                            checked={form.assigned_devices.some(
                                                (device) => device.uuid === item.raw.uuid
                                            )}
                                        />
                                        <ListItemText primary={item.devicename} />
                                    </MenuItem>
                                ))}
                            </TextField>

                            {editingUser && (
                                <TextField
                                    select
                                    label="Role"
                                    fullWidth
                                    value={form.role}
                                    onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                                >
                                    <MenuItem value="USER">User</MenuItem>
                                    <MenuItem value="ADMIN">Admin</MenuItem>
                                </TextField>
                            )}

                            {!editingUser && (
                                <TextField
                                    label="Password"
                                    type="password"
                                    fullWidth
                                    required
                                    inputProps={{ minLength: 6 }}
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm((p) => ({ ...p, password: e.target.value }))
                                    }
                                />
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5 }}>
                        <Button
                            onClick={closeForm}
                            disabled={submitting}
                            sx={{ textTransform: "none", fontWeight: 600, color: "#4B5563" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            disabled={submitting}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "10px",
                                background: COLORS.total.main,
                                "&:hover": { background: "#4338CA" },
                            }}
                        >
                            {submitting
                                ? "Saving…"
                                : editingUser
                                    ? "Update User"
                                    : "Create User"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};

export default UserManagement;